import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Save,
  Trash2,
  Pencil,
  RotateCcw,
  ExternalLink,
  ClipboardList,
} from "lucide-react";
import {
  ClientRecordFormConfig,
  FormField,
  FieldType,
  defaultFormConfig,
  loadFormConfig,
  saveFormConfig,
} from "@/lib/client-record-form";

const TYPE_LABELS: Record<FieldType, string> = {
  text: "Texto curto",
  textarea: "Texto longo",
  email: "Email",
  phone: "Telemóvel",
  date: "Data",
  number: "Número",
  switch: "Sim / Não (Toggle)",
  checkbox_group: "Múltipla escolha (caixas)",
  yes_no: "Assinalar Sim ou Não",
  chips: "Escolha única (botões)",
  select: "Lista suspensa",
  consent: "Consentimento",
  signature: "Assinatura",
  password: "Palavra-passe",
};

const CUSTOM_TYPES: FieldType[] = ["text", "textarea", "email", "phone", "date", "number", "switch", "yes_no", "checkbox_group", "chips", "select"];

const slugify = (s: string) =>
  "custom_" +
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);

const ClientRecordFormAdmin = () => {
  const { toast } = useToast();
  const [config, setConfig] = useState<ClientRecordFormConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState<string | null>(null);

  useEffect(() => {
    loadFormConfig().then(setConfig);
  }, []);

  if (!config) {
    return <div className="p-6 text-sm text-muted-foreground">A carregar...</div>;
  }

  const update = (fn: (c: ClientRecordFormConfig) => void) => {
    setConfig((prev) => {
      const next: ClientRecordFormConfig = JSON.parse(JSON.stringify(prev));
      fn(next);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    const { error } = await saveFormConfig(config);
    setSaving(false);
    if (error) toast({ title: "Erro ao guardar", description: error.message, variant: "destructive" });
    else toast({ title: "Ficha atualizada", description: "As alterações já estão visíveis para os clientes." });
  };

  const resetAll = async () => {
    const def = defaultFormConfig();
    setConfig(def);
    await saveFormConfig(def);
    toast({ title: "Ficha reposta", description: "Voltámos ao formulário padrão." });
  };

  const moveField = (sIdx: number, fIdx: number, dir: -1 | 1) =>
    update((c) => {
      const fields = c.sections[sIdx].fields;
      const target = fIdx + dir;
      if (target < 0 || target >= fields.length) return;
      [fields[fIdx], fields[target]] = [fields[target], fields[fIdx]];
    });

  const moveSection = (sIdx: number, dir: -1 | 1) =>
    update((c) => {
      const target = sIdx + dir;
      if (target < 0 || target >= c.sections.length) return;
      [c.sections[sIdx], c.sections[target]] = [c.sections[target], c.sections[sIdx]];
    });

  const addCustomField = (sIdx: number) =>
    update((c) => {
      let key = slugify("novo campo " + (c.sections[sIdx].fields.length + 1));
      const all = c.sections.flatMap((s) => s.fields.map((f) => f.key));
      while (all.includes(key)) key += "_1";
      c.sections[sIdx].fields.push({
        key,
        label: "Novo campo",
        type: "text",
        enabled: true,
        system: false,
      });
    });

  const addSection = () =>
    update((c) => {
      c.sections.push({
        id: "custom_" + Date.now(),
        title: "Nova secção",
        enabled: true,
        fields: [],
      });
    });

  const patchField = (sIdx: number, fIdx: number, patch: Partial<FormField>) =>
    update((c) => {
      c.sections[sIdx].fields[fIdx] = { ...c.sections[sIdx].fields[fIdx], ...patch };
    });

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <ClipboardList size={22} className="text-primary" /> Editor da Ficha do Cliente
          </h1>
          <p className="text-sm text-muted-foreground">
            Escolha o que aparece na ficha, edite os textos e crie os seus próprios campos.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild className="rounded-full">
            <a href="/ficha" target="_blank" rel="noopener noreferrer">
              <ExternalLink size={16} className="mr-2" /> Ver ficha
            </a>
          </Button>
          <Button variant="ghost" className="rounded-full" onClick={resetAll}>
            <RotateCcw size={16} className="mr-2" /> Repor padrão
          </Button>
          <Button className="rounded-full" onClick={save} disabled={saving}>
            <Save size={16} className="mr-2" /> {saving ? "A guardar..." : "Guardar"}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
        <div className="space-y-2">
          <Label>Texto de introdução da ficha</Label>
          <Textarea rows={3} value={config.intro} onChange={(e) => update((c) => { c.intro = e.target.value; })} />
        </div>
        <div className="space-y-2">
          <Label>Texto do consentimento informado (RGPD)</Label>
          <Textarea rows={6} value={config.consent_text} onChange={(e) => update((c) => { c.consent_text = e.target.value; })} />
        </div>
      </div>

      {config.sections.map((section, sIdx) => (
        <div key={section.id} className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="flex items-center gap-3 p-4 bg-muted/40">
            <Switch
              checked={section.enabled}
              onCheckedChange={(v) => update((c) => { c.sections[sIdx].enabled = v; })}
            />
            <Input
              value={section.title}
              onChange={(e) => update((c) => { c.sections[sIdx].title = e.target.value; })}
              className="max-w-xs font-medium"
            />
            <span className="text-xs text-muted-foreground hidden sm:inline">
              {section.fields.filter((f) => f.enabled).length}/{section.fields.length} campos ativos
            </span>
            <div className="ml-auto flex items-center gap-1">
              <Button size="icon" variant="ghost" onClick={() => moveSection(sIdx, -1)}><ChevronUp size={16} /></Button>
              <Button size="icon" variant="ghost" onClick={() => moveSection(sIdx, 1)}><ChevronDown size={16} /></Button>
              {!section.fields.some((f) => f.system) && (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => update((c) => { c.sections.splice(sIdx, 1); })}
                >
                  <Trash2 size={16} className="text-destructive" />
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setOpen((o) => ({ ...o, [section.id]: !o[section.id] }))}
              >
                {open[section.id] ? "Fechar" : "Abrir"}
              </Button>
            </div>
          </div>

          {open[section.id] && (
            <div className="p-4 space-y-2">
              {section.fields.map((field, fIdx) => (
                <div key={field.key} className="rounded-xl border border-border p-3 space-y-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Switch
                      checked={field.enabled}
                      onCheckedChange={(v) => patchField(sIdx, fIdx, { enabled: v })}
                    />
                    <span className="text-sm font-medium flex-1 min-w-[150px]">
                      {field.label}
                      {field.required && <span className="text-destructive"> *</span>}
                    </span>
                    <span className="text-[11px] rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                      {TYPE_LABELS[field.type]}
                    </span>
                    {(field.key === "staff_name" || !field.system) && (
                      <span className="text-[11px] rounded-full bg-primary/10 text-primary px-2 py-0.5">Editável</span>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => moveField(sIdx, fIdx, -1)}><ChevronUp size={15} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => moveField(sIdx, fIdx, 1)}><ChevronDown size={15} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => setEditing(editing === field.key ? null : field.key)}>
                      <Pencil size={15} />
                    </Button>
                    {(field.key === "staff_name" || !field.system) && (
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        onClick={() => update((c) => { c.sections[sIdx].fields.splice(fIdx, 1); })}
                        title="Excluir campo"
                      >
                        <Trash2 size={15} className="text-destructive" />
                      </Button>
                    )}
                  </div>

                  {editing === field.key && (
                    <div className="space-y-3 border-t border-border pt-3">
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Etiqueta / pergunta</Label>
                          <Input value={field.label} onChange={(e) => patchField(sIdx, fIdx, { label: e.target.value })} />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Tipo de campo</Label>
                          {field.system && field.key !== "staff_name" ? (
                            <Input value={TYPE_LABELS[field.type]} disabled />
                          ) : (
                            <Select value={field.type} onValueChange={(v) => patchField(sIdx, fIdx, { type: v as FieldType })}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {CUSTOM_TYPES.map((t) => (
                                  <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Texto de ajuda</Label>
                          <Input value={field.hint || ""} onChange={(e) => patchField(sIdx, fIdx, { hint: e.target.value })} />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Placeholder</Label>
                          <Input value={field.placeholder || ""} onChange={(e) => patchField(sIdx, fIdx, { placeholder: e.target.value })} />
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium flex items-center gap-1.5 text-primary">
                            Exibição condicional
                          </Label>
                          <Select 
                            value={field.showIf || "none"} 
                            onValueChange={(v) => patchField(sIdx, fIdx, { showIf: v === "none" ? undefined : v })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Sempre visível" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Sempre visível</SelectItem>
                              {config.sections.flatMap(s => s.fields)
                                .filter(f => (f.type === 'yes_no' || f.type === 'switch') && f.key !== field.key)
                                .map(f => (
                                  <SelectItem key={f.key} value={f.key}>
                                    Mostrar se "{f.label}" for Sim
                                  </SelectItem>
                                ))
                              }
                            </SelectContent>
                          </Select>
                          <p className="text-[10px] text-muted-foreground italic">
                            O campo só aparecerá se a pergunta acima for respondida com "Sim".
                          </p>
                        </div>
                      </div>

                      {["full_name", "email", "phone", "consent_accepted", "signature_data", "password"].includes(field.key) ? (
                        <p className="text-[11px] text-muted-foreground">Este campo é sempre obrigatório quando ativo.</p>
                      ) : (
                        <label className="flex items-center gap-2 text-sm">
                          <Switch checked={!!field.required} onCheckedChange={(v) => patchField(sIdx, fIdx, { required: v })} />
                          Campo obrigatório
                        </label>
                      )}

                      {["checkbox_group", "chips", "select", "yes_no"].includes(field.type) && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs">Opções (uma por linha)</Label>
                            {field.type === "yes_no" && (
                              <span className="text-[10px] text-muted-foreground italic">Deixe em branco para Sim/Não padrão</span>
                            )}
                          </div>
                          <Textarea
                            rows={3}
                            placeholder={field.type === "yes_no" ? "Sim\nNão" : "Opção 1\nOpção 2"}
                            value={(field.options || []).join("\n")}
                            onChange={(e) =>
                              patchField(sIdx, fIdx, {
                                options: e.target.value.split("\n").map((o) => o.trim()).filter(Boolean),
                              })
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              <Button variant="outline" size="sm" className="rounded-full" onClick={() => addCustomField(sIdx)}>
                <Plus size={15} className="mr-1.5" /> Adicionar campo
              </Button>
            </div>
          )}
        </div>
      ))}

      <div className="flex justify-between gap-3">
        <Button variant="outline" className="rounded-full" onClick={addSection}>
          <Plus size={16} className="mr-2" /> Adicionar secção
        </Button>
        <Button className="rounded-full" onClick={save} disabled={saving}>
          <Save size={16} className="mr-2" /> {saving ? "A guardar..." : "Guardar alterações"}
        </Button>
      </div>
    </div>
  );
};

export default ClientRecordFormAdmin;
